import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { X } from "lucide-react";
import { toast } from "sonner";
import LocationSearch from "@/components/app/LocationSearch";
import { SPORTS, LEVELS } from "@/lib/games";

function Chips({ options, value, onChange, testid }) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => (
        <button type="button" key={o.k} onClick={() => onChange(o.k)} data-testid={`${testid}-${o.k}`}
          className={`press h-9 px-3 rounded-full text-sm border ${value === o.k ? "bg-emerald-500 text-black border-emerald-500" : "border-white/10 bg-black/30"}`}>
          {o.icon ? `${o.icon} ` : ""}{o.label}
        </button>
      ))}
    </div>
  );
}

function F({ label, children }) {
  return <div className="space-y-1.5"><Label className="text-xs">{label}</Label>{children}</div>;
}

export default function CreateGameSheet({ onClose, onCreated }) {
  const today = new Date().toLocaleDateString("sv-SE");
  const [f, setF] = useState({ sport: "society", name: "", date: today, start_time: "19:00", end_time: "21:00", total_slots: "10", missing_slots: "2", level: "resenha", position: "", price: "", description: "", private: false });
  const [place, setPlace] = useState("");
  const [loc, setLoc] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });

  const submit = async (e) => {
    e.preventDefault();
    if (!loc) { toast.error("Marque o local do jogo no mapa."); return; }
    setBusy(true);
    try {
      await api.post("/games", { ...f, total_slots: Number(f.total_slots), missing_slots: Number(f.missing_slots), lat: loc.lat, lng: loc.lng,
        address: place || loc.label || "", position: f.position || null, price: f.price || null });
      toast.success("Jogo publicado! Bora fechar o time.");
      onCreated?.();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  return (
    <Sheet open onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <SheetContent side="bottom" className="max-h-[88dvh] overflow-y-auto rounded-t-3xl bg-card border-t border-white/10" data-testid="create-game-sheet">
        <button type="button" onClick={onClose} className="absolute top-3 right-4 h-9 w-9 rounded-full bg-muted grid place-items-center press z-10" data-testid="create-game-close"><X className="w-4 h-4" /></button>
        <SheetHeader className="text-left">
          <SheetTitle className="text-2xl heading font-extrabold">Fechar time</SheetTitle>
          <p className="text-xs text-muted-foreground">O jogo vale por um dia: some do mapa no horário de término.</p>
        </SheetHeader>
        <form onSubmit={submit} className="mt-4 space-y-4 pb-24">
          <F label="Esporte"><Chips options={SPORTS} value={f.sport} onChange={set("sport")} testid="game-sport" /></F>
          <F label="Nome do jogo"><Input value={f.name} onChange={set("name")} required minLength={2} maxLength={80} placeholder="ex: Society de quinta" data-testid="game-name-input" /></F>
          <div className="rounded-xl border border-white/10 bg-muted/30 p-3 space-y-2">
            <F label="Local (quadra, rua e número)"><Input value={place} onChange={(e) => setPlace(e.target.value)} placeholder="ex: Rua São Luiz, 500" data-testid="game-place-input" /></F>
            <LocationSearch query={place} value={loc} onChange={setLoc} hint="Confira se o pin está na quadra certa." testid="game-location" />
            <label className="flex items-center gap-3 text-sm pt-1">
              <Switch checked={f.private} onCheckedChange={set("private")} data-testid="game-private-switch" /> Local particular
            </label>
            {f.private && <p className="text-[11px] text-muted-foreground">O endereço exato só aparece para quem entrar no jogo.</p>}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="col-span-2"><F label="Data"><Input type="date" value={f.date} onChange={set("date")} min={today} required data-testid="game-date-input" /></F></div>
            <F label="Início"><Input type="time" value={f.start_time} onChange={set("start_time")} required data-testid="game-start-input" /></F>
            <F label="Término"><Input type="time" value={f.end_time} onChange={set("end_time")} required data-testid="game-end-input" /></F>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <F label="Vagas totais"><Input type="number" min="2" max="200" value={f.total_slots} onChange={set("total_slots")} required data-testid="game-total-input" /></F>
            <F label="Vagas que faltam"><Input type="number" min="1" max="200" value={f.missing_slots} onChange={set("missing_slots")} required data-testid="game-missing-input" /></F>
          </div>
          <F label="Nível"><Chips options={LEVELS} value={f.level} onChange={set("level")} testid="game-level" /></F>
          <div className="grid grid-cols-2 gap-2">
            <F label="Posição que falta (opcional)"><Input value={f.position} onChange={set("position")} maxLength={60} placeholder="ex: goleiro" data-testid="game-position-input" /></F>
            <F label="Rateio (opcional)"><Input value={f.price} onChange={set("price")} maxLength={40} placeholder="ex: R$ 15,00" data-testid="game-price-input" /></F>
          </div>
          <F label="Descrição"><Textarea value={f.description} onChange={set("description")} maxLength={600} rows={2} data-testid="game-description-input" /></F>
          <p className="text-[11px] text-muted-foreground">Esporte é por conta e risco de cada participante. O organizador é responsável pelo jogo.</p>
          <Button type="submit" disabled={busy} className="w-full h-12 text-base bg-emerald-500 text-black hover:bg-emerald-400 press" data-testid="game-submit-button">{busy ? "Publicando..." : "Publicar jogo"}</Button>
        </form>
      </SheetContent>
    </Sheet>
  );
}
